// src/components1/HeroTrendChart.js
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

/**
 * Format currency amount for compact display above bars:
 * e.g. $0, $450, $1.2k, $6.7k, $12.5k
 */
const formatCompactAmount = (amount, currency = "$") => {
  if (!amount || amount === 0) return `${currency}0`;
  if (amount >= 1000000) {
    return `${currency}${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    const formatted = (amount / 1000).toFixed(1);
    return `${currency}${formatted.endsWith(".0") ? formatted.slice(0, -2) : formatted}k`;
  }
  return `${currency}${Math.round(amount)}`;
};

/**
 * HeroTrendChart:
 * Displays a combined bar chart + smooth curved trend line with monthly total values.
 * Allows horizontal scrolling when more than 5 months are present.
 */
const HeroTrendChart = ({ data = [], currency = "$", selectedMonth, onSelectMonth }) => {
  const [activeIdx, setActiveIdx] = useState(null);
  const scrollContainerRef = useRef(null);

  // Grow the chart in instead of snapping from the empty state to the real values.
  const [animateIn, setAnimateIn] = useState(false);
  const frameRef = useRef(0);
  const hasIntroPlayedRef = useRef(false);

  // Dynamic width based on number of months
  const monthSlotWidth = 62;
  const paddingLeft = 32;
  const paddingRight = 32;
  const minWidth = 340;
  const svgWidth = Math.max(
    minWidth,
    paddingLeft + paddingRight + Math.max(0, data.length - 1) * monthSlotWidth
  );
  const svgHeight = 126;
  const usableWidth = svgWidth - paddingLeft - paddingRight;

  const topY = 22; // Topmost Y for max value
  const baselineY = 92; // Bottom baseline for bars
  const chartHeight = baselineY - topY; // 70px

  const maxTotal = Math.max(...data.map((d) => d.total || 0), 1);

  // Restart the grow-in whenever the plotted values change
  const dataSignature = data.map((d) => `${d.yearMonth}:${d.total}`).join("|");
  const hasPlottableData = data.some((d) => (d.total || 0) > 0);
  // collapse before paint so the first frame shows the un-drawn line
  useLayoutEffect(() => {
    // Nothing to draw yet (first mount often has no data): stay in the shown
    // state so the intro is not spent on an empty chart.
    if (!hasPlottableData) {
      setAnimateIn(true);
      return undefined;
    }
    // Play the full intro once, on the first render that actually has values.
    // Later updates (adding an expense, switching month) move straight to the
    // new values instead of replaying for four seconds.
    if (hasIntroPlayedRef.current) {
      setAnimateIn(true);
      return undefined;
    }
    hasIntroPlayedRef.current = true;
    setAnimateIn(false);
    // two frames so the browser paints the collapsed state before transitioning
    const outer = requestAnimationFrame(() => {
      const inner = requestAnimationFrame(() => setAnimateIn(true));
      frameRef.current = inner;
    });
    frameRef.current = outer;
    return () => cancelAnimationFrame(frameRef.current);
  }, [dataSignature, hasPlottableData]);

  // Auto-scroll to the right so current month is visible by default
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, [data]);

  // If selectedMonth changes to 'current', scroll smoothly to current bar
  useEffect(() => {
    if (selectedMonth === "current" && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
      const cur = data.find((d) => d.isCurrent) || data[data.length - 1];
      if (cur) {
        const curIdx = data.indexOf(cur);
        setActiveIdx(curIdx);
      }
    }
  }, [selectedMonth, data]);

  const scrollToCurrent = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
    const currentItem = data.find((d) => d.isCurrent) || data[data.length - 1];
    if (currentItem) {
      const idx = data.indexOf(currentItem);
      setActiveIdx(idx);
      if (onSelectMonth) {
        onSelectMonth(currentItem.yearMonth || "current");
      }
    }
  };

  if (!data || data.length === 0) return null;

  // Compute (x, y) coordinates for each month
  const points = data.map((item, index) => {
    const x =
      data.length === 1
        ? paddingLeft + usableWidth / 2
        : paddingLeft + (index / (data.length - 1)) * usableWidth;
    // Normalize total: even $0 gets a subtle minimum height (4px above baseline)
    const normalizedRatio = item.total > 0 ? item.total / maxTotal : 0;
    const barHeight = Math.max(4, normalizedRatio * chartHeight);
    const y = baselineY - barHeight;

    return {
      ...item,
      x,
      y,
      barHeight,
      index,
    };
  });

  // Generate smooth cubic Bezier curve through all points
  let curvePath = "";
  if (points.length > 0) {
    curvePath = `M ${points[0].x},${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const pCurrent = points[i];
      const pNext = points[i + 1];
      const dx = (pNext.x - pCurrent.x) / 2;
      curvePath += ` C ${pCurrent.x + dx},${pCurrent.y} ${pNext.x - dx},${pNext.y} ${pNext.x},${pNext.y}`;
    }
  }

  // Area fill under the curved line
  const lastPoint = points[points.length - 1];
  const firstPoint = points[0];
  const areaPath = `${curvePath} L ${lastPoint.x},${baselineY} L ${firstPoint.x},${baselineY} Z`;

  const barWidth = 20;

  // The bars grow first; the curve draws once they have settled, then the labels.
  const barStagger = 45;
  const barDuration = 620;
  const barsFinishAt = Math.max(0, points.length - 1) * barStagger + barDuration;
  const lineDelay = barsFinishAt + 60;
  const lineDuration = 900;
  // Each month label rises with its own bar; the amounts and dots appear as the
  // pen draws past them.
  const labelFade = 300;
  const lastIndex = Math.max(1, points.length - 1);
  const monthDelayFor = (index) => index * barStagger;
  const penDelayFor = (index) => lineDelay + (index / lastIndex) * lineDuration;

  return (
    <div style={{ width: "100%", marginTop: "10px", position: "relative" }}>
      <div
        ref={scrollContainerRef}
        style={{
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div style={{ width: svgWidth, minWidth: "100%" }}>
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            style={{ width: "100%", height: "auto", overflow: "visible", display: "block" }}
          >
            <defs>
              {/* Curved Line Gradient */}
              <linearGradient id="heroLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#C9A3D4" />
                <stop offset="50%" stopColor="#D4552A" />
                <stop offset="100%" stopColor="#B84318" />
              </linearGradient>

              {/* Area Fill Gradient under the Curve */}
              <linearGradient id="heroAreaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#D4552A" stopOpacity="0.32" />
                <stop offset="80%" stopColor="#D4552A" stopOpacity="0.04" />
                <stop offset="100%" stopColor="#D4552A" stopOpacity="0.0" />
              </linearGradient>

              {/* Active Bar Gradient */}
              <linearGradient id="activeBarGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#D4552A" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#B84318" stopOpacity="0.4" />
              </linearGradient>

              {/* Inactive Bar Gradient */}
              <linearGradient id="normalBarGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.06" />
              </linearGradient>

              {/* Subtle Glow Filter */}
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

        {/* 1. Subtle horizontal dashed grid lines */}
        <line
          x1={paddingLeft - 10}
          y1={baselineY}
          x2={svgWidth - paddingRight + 10}
          y2={baselineY}
          stroke="rgba(255, 255, 255, 0.12)"
          strokeWidth="1"
        />
        <line
          x1={paddingLeft - 10}
          y1={topY}
          x2={svgWidth - paddingRight + 10}
          y2={topY}
          stroke="rgba(255, 255, 255, 0.08)"
          strokeDasharray="3 3"
          strokeWidth="1"
        />

        {/* 2. Soft Area Fill under the Curved Line */}
        <path
          d={areaPath}
          fill="url(#heroAreaGrad)"
          style={{ opacity: animateIn ? 1 : 0, transition: `opacity 700ms ease-out ${lineDelay}ms` }}
        />

        {/* 3. Vertical Bars for each Month */}
        {points.map((p) => {
          const isHighlighted = activeIdx === p.index || (activeIdx === null && p.isCurrent);
          return (
            <g
              key={p.index}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setActiveIdx(p.index)}
              onMouseLeave={() => setActiveIdx(null)}
              onClick={() => onSelectMonth && onSelectMonth(p.yearMonth)}
            >
              {/* Vertical rounded bar */}
              <rect
                style={{
                  transformBox: "fill-box",
                  transformOrigin: "bottom",
                  transform: animateIn ? "scaleY(1)" : "scaleY(0.001)",
                  transition: `transform ${barDuration}ms cubic-bezier(0.22, 1, 0.36, 1) ${p.index * barStagger}ms, fill 250ms ease, stroke 250ms ease`,
                }}
                x={p.x - barWidth / 2}
                y={p.y}
                width={barWidth}
                height={p.barHeight}
                rx="6"
                ry="6"
                fill={isHighlighted ? "url(#activeBarGrad)" : "url(#normalBarGrad)"}
                stroke={isHighlighted ? "rgba(239, 135, 103, 0.6)" : "transparent"}
                strokeWidth="1"
              />
            </g>
          );
        })}

        {/* 4. Smooth Curved Line */}
        <path
          d={curvePath}
          fill="none"
          stroke="url(#heroLineGrad)"
          strokeWidth="3"
          strokeLinecap="round"
          filter="url(#glow)"
          pathLength="1"
          strokeDasharray="1"
          strokeDashoffset={animateIn ? 0 : 1}
          style={{ transition: `stroke-dashoffset ${lineDuration}ms cubic-bezier(0.33, 1, 0.68, 1) ${lineDelay}ms` }}
        />

        {/* 5. Points / Dots & Value Labels */}
        {points.map((p) => {
          const isHighlighted = activeIdx === p.index || (activeIdx === null && p.isCurrent);
          return (
            <g
              key={p.index}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setActiveIdx(p.index)}
              onMouseLeave={() => setActiveIdx(null)}
              onClick={() => onSelectMonth && onSelectMonth(p.yearMonth)}
            >
              {/* Outer Glow Halo for active point */}
              {isHighlighted && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="9"
                  fill="rgba(212, 85, 42, 0.35)"
                  style={{ opacity: animateIn ? 1 : 0, transition: `opacity ${labelFade}ms ease-out ${penDelayFor(p.index)}ms` }}
                />
              )}

              {/* Data Node Circle */}
              <circle
                cx={p.x}
                cy={p.y}
                r={isHighlighted ? "5" : "3.5"}
                fill={isHighlighted ? "#D4552A" : "#FFFFFF"}
                stroke={isHighlighted ? "#FFFFFF" : "rgba(66, 34, 74, 0.6)"}
                strokeWidth="2"
                style={{
                  opacity: animateIn ? 1 : 0,
                  transition: `fill 0.2s ease, stroke 0.2s ease, r 0.2s ease, opacity ${labelFade}ms ease-out ${penDelayFor(p.index)}ms`,
                }}
              />

              {/* Total Value Label above bar */}
              <text
                x={p.x}
                y={Math.max(14, p.y - 9)}
                textAnchor="middle"
                fill={isHighlighted ? "#FFFFFF" : "rgba(255, 255, 255, 0.75)"}
                fontSize={isHighlighted ? "10.5" : "9.5"}
                fontWeight={isHighlighted ? "700" : "600"}
                fontFamily="'Poppins', sans-serif"
                style={{
                  opacity: animateIn ? 1 : 0,
                  transition: `fill 0.2s ease, opacity ${labelFade}ms ease-out ${penDelayFor(p.index)}ms`,
                  userSelect: "none",
                }}
              >
                {formatCompactAmount(p.total, currency)}
              </text>

              {/* Month Label below baseline */}
              <text
                x={p.x}
                y={baselineY + 18}
                textAnchor="middle"
                fill={isHighlighted ? "#FFFFFF" : "rgba(255, 255, 255, 0.6)"}
                fontSize="11"
                fontWeight={isHighlighted ? "700" : "500"}
                fontFamily="'Poppins', sans-serif"
                style={{
                  opacity: animateIn ? 1 : 0,
                  transition: `fill 0.2s ease, opacity ${labelFade}ms ease-out ${monthDelayFor(p.index)}ms`,
                  userSelect: "none",
                }}
              >
                {p.label}
              </text>
            </g>
          );
        })}
          </svg>
        </div>
      </div>
      {data.length > 4 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "8px",
            padding: "0 4px",
            fontSize: "11px",
            color: "rgba(255, 255, 255, 0.75)",
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({ left: 0, behavior: "smooth" });
              }
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255, 255, 255, 0.75)",
              padding: "2px 4px",
              fontSize: "11px",
              fontWeight: 500,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
            title="View past months"
          >
            <i className="fi fi-rr-arrow-small-left" style={{ fontSize: "14px", verticalAlign: "middle" }}></i>
            <span>Past months</span>
          </button>

          <button
            type="button"
            onClick={scrollToCurrent}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "1px solid rgba(255, 255, 255, 0.4)",
              color: "#FFFFFF",
              borderRadius: "14px",
              padding: "3px 12px",
              fontSize: "11px",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              transition: "transform 0.15s ease, background 0.15s ease",
            }}
            title="Scroll to current month bar"
          >
            <span>Current</span>
            <i className="fi fi-rr-check" style={{ fontSize: "9.5px", color: "var(--accent-coral)" }}></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default HeroTrendChart;
